import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from 'react-router-dom';
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { DateField } from '@/components/form/fields/DateField';
import { MunicipalitySelect } from './MunicipalitySelect';
import { useMunicipalities } from '../hooks/useMunicipalities';
import { useCreateMunicipalList, useUpdateMunicipalList } from '../hooks/useMunicipalListMutations';
import { municipalListSchema, type MunicipalListFormValues } from '../schemas/municipal-list.schema';
import { toMunicipalListFormValues, toMunicipalListPayload } from '../utils/form-mapping.utils';
import { getApiErrorMessage, getApiErrorStatus } from '../utils/api-error.utils';
import { preRegistrationRoutes } from '../utils/routes.utils';
import type { MunicipalListDetail } from '../types/pre-registration.types';

interface MunicipalListHeaderFormProps {
  // Without a list the form creates one
  list?: MunicipalListDetail;
  readOnly?: boolean;
  // Shown next to the save button (e.g. the list's audit info)
  footer?: React.ReactNode;
}

// Same geometry as the form fields, so the grid rows line up
const ReadOnlyValue: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <FormItem>
    <Label>{label}</Label>
    <Input value={value} disabled readOnly />
  </FormItem>
);

export const MunicipalListHeaderForm: React.FC<MunicipalListHeaderFormProps> = ({
  list,
  readOnly = false,
  footer,
}) => {
  const navigate = useNavigate();
  const { data: municipalities = [] } = useMunicipalities();
  const createMutation = useCreateMunicipalList();
  const updateMutation = useUpdateMunicipalList(list?.id ?? '');
  const isSaving = createMutation.isPending || updateMutation.isPending;

  const form = useForm<MunicipalListFormValues>({
    resolver: zodResolver(municipalListSchema),
    defaultValues: toMunicipalListFormValues(list),
  });

  // Sync with the saved header only when it changes, so row reloads keep unsaved edits
  const listId = list?.id;
  const listUpdatedAt = list?.updatedAt;
  useEffect(() => {
    if (list) form.reset(toMunicipalListFormValues(list));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, listUpdatedAt]);

  const selectedId = form.watch('municipalityId');
  const selected = municipalities.find((item) => String(item.id) === selectedId);
  // The backend rejects a municipality change while some row has a locality
  const municipalityLocked = !!list?.accommodations.some((row) => row.localityId !== null);

  const onSubmit = (values: MunicipalListFormValues) => {
    const payload = toMunicipalListPayload(values);
    if (!list) {
      createMutation.mutate(payload, {
        onSuccess: (created) => navigate(preRegistrationRoutes.municipalList(created.id)),
      });
      return;
    }
    updateMutation.mutate(payload, {
      onError: (error) => {
        if (getApiErrorStatus(error) === 409) {
          form.setError('municipalityId', { message: getApiErrorMessage(error, '') });
        }
      },
    });
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <FormField
            control={form.control}
            name="municipalityId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Municipio <span className="text-red-500">*</span>
                </FormLabel>
                <FormControl>
                  <MunicipalitySelect
                    value={field.value ? Number(field.value) : undefined}
                    onChange={(municipalityId) => field.onChange(municipalityId ? String(municipalityId) : '')}
                    allowAll={false}
                    showKind
                    disabled={readOnly || municipalityLocked}
                  />
                </FormControl>
                {municipalityLocked && !readOnly && (
                  <FormDescription>
                    No se puede cambiar: hay alojamientos con localidad cargada.
                  </FormDescription>
                )}
                <FormMessage />
              </FormItem>
            )}
          />
          <ReadOnlyValue label="Departamento" value={selected?.department.name ?? '—'} />
          <ReadOnlyValue label="Región" value={selected?.department.region.name ?? '—'} />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {/* Laid out like FormItem (inline label, space-y-2) to line up with the field next to it */}
          <DateField
            name="surveyDate"
            label="Fecha de relevamiento"
            disabled={readOnly}
            icon={null}
            className="mb-0 space-y-2"
            labelClassName="inline mb-0"
          />
          <FormField
            control={form.control}
            name="listManagerName"
            render={({ field }) => (
              <FormItem className="md:col-span-2">
                <FormLabel>Responsable municipal de la carga</FormLabel>
                <FormControl>
                  <Input {...field} disabled={readOnly} maxLength={100} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="observations"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Observaciones</FormLabel>
              <FormControl>
                <Textarea {...field} disabled={readOnly} rows={3} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {(footer || !readOnly) && (
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>{footer}</div>
            {!readOnly && (
              <Button type="submit" disabled={isSaving} className="self-end sm:self-auto">
                {isSaving ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Save className="mr-2 h-4 w-4" />
                )}
                {list ? 'Guardar cambios' : 'Crear listado'}
              </Button>
            )}
          </div>
        )}
      </form>
    </Form>
  );
};
